import React from 'react';
import { motion } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import {
  CodeBracketIcon,
  MagnifyingGlassIcon,
  CogIcon,
  ServerIcon,
  CircleStackIcon,
  WrenchScrewdriverIcon,
  ArrowUpIcon,
} from '@heroicons/react/24/outline';

const Footer = () => {
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToSection = (href) => {
    if (isHomePage) {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      window.location.href = `/${href}`;
    }
  };

  const services = [
    { name: 'Web Development', href: '/services/web-development', icon: CodeBracketIcon },
    { name: 'SEO Optimization', href: '/services/seo-optimization', icon: MagnifyingGlassIcon },
    { name: 'IT Consulting', href: '/services/it-consulting', icon: CogIcon },
    { name: 'System Management', href: '/services/system-management', icon: ServerIcon },
    { name: 'Data Processing', href: '/services/data-processing', icon: CircleStackIcon },
    { name: 'Custom Software', href: '/services/custom-software', icon: WrenchScrewdriverIcon },
  ];

  const companyLinks = [
    { name: 'Home', href: '/' },
    { name: 'About Us', href: '#about' },
    { name: 'Our Services', href: '/services' },
    { name: 'Contact', href: '#contact' },
  ];

  return (
    <footer className="bg-gray-900 text-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900"></div>
      <div className="absolute inset-0 bg-grid-white/5"></div>
      
      <div className="relative z-10">
        {/* Main Footer Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 items-start">
            
            {/* Column 1: Logo Only */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="h-48 flex items-center justify-center"
            >
              <motion.div
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.2 }}
                className="w-full h-full flex items-center justify-center"
              >
                <img
                  src="/brightcode_logo.png"
                  alt="BrightCode"
                  className="w-full h-full object-contain"
                />
              </motion.div>
            </motion.div>

            {/* Column 2: Text Section */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <h3 className="text-2xl font-bold bg-gradient-to-r from-green-400 to-green-600 bg-clip-text text-transparent mb-4">
                BrightCode
              </h3>
              <p className="text-gray-300 text-base leading-relaxed">
                Innovative IT solutions for modern business success. We transform your digital vision into reality.
              </p>
            </motion.div>


            {/* Column 3: Services Links */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <h4 className="text-xl font-bold mb-6 text-white">Our Services</h4>
              <ul className="space-y-3">
                {services.map((service, index) => {
                  const IconComponent = service.icon;
                  return (
                    <motion.li
                      key={index}
                      whileHover={{ x: 5 }}
                      transition={{ duration: 0.2 }}
                    >
                      <Link
                        to={service.href}
                        className="flex items-center text-gray-200 hover:text-green-300 transition-colors duration-200 group"
                      >
                        <div className="w-6 h-6 rounded-md bg-gray-800 group-hover:bg-green-600/20 flex items-center justify-center mr-3 transition-all duration-200">
                          <IconComponent className="w-4 h-4 text-gray-400 group-hover:text-green-400 transition-colors duration-200" />
                        </div>
                        <span className="text-sm">{service.name}</span>
                      </Link>
                    </motion.li>
                  );
                })}
              </ul>
            </motion.div>

            {/* Column 4: Company Links */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <h4 className="text-xl font-bold mb-6 text-white">Company</h4>
              <ul className="space-y-3">
                {companyLinks.map((link, index) => (
                  <motion.li
                    key={index}
                    whileHover={{ x: 5 }}
                    transition={{ duration: 0.2 }}
                  >
                    {link.href.startsWith('#') ? (
                      <button
                        onClick={() => scrollToSection(link.href)}
                        className="text-gray-200 hover:text-green-300 transition-colors duration-200 flex items-center group"
                      >
                        <div className="w-2 h-2 bg-green-600/60 rounded-full mr-3 group-hover:bg-green-400 transition-all duration-200"></div>
                        <span className="text-sm">{link.name}</span>
                      </button>
                    ) : (
                      <Link
                        to={link.href}
                        className="text-gray-200 hover:text-green-300 transition-colors duration-200 flex items-center group"
                      >
                        <div className="w-2 h-2 bg-green-600/60 rounded-full mr-3 group-hover:bg-green-400 transition-all duration-200"></div>
                        <span className="text-sm">{link.name}</span>
                      </Link>
                    )}
                  </motion.li>
                ))}
              </ul>
            </motion.div>

          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <motion.p
                className="text-gray-400 text-sm mb-4 md:mb-0"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                &copy; 2025 BrightCode. All rights reserved.
              </motion.p>
              
              <div className="flex items-center space-x-6">
                <motion.button
                  onClick={scrollToTop}
                  className="p-2 bg-gray-800 hover:bg-gray-700 rounded-full transition-colors duration-200 group"
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  title="Back to top"
                >
                  <ArrowUpIcon className="w-5 h-5 text-gray-400 group-hover:text-green-400 transition-colors duration-200" />
                </motion.button>
              </div>
            </div>
          </div>
        </div>

        {/* Decorative Elements */}
        <div className="absolute top-20 right-20 w-32 h-32 bg-gradient-to-br from-green-600/10 to-green-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-20 w-40 h-40 bg-gradient-to-br from-green-500/10 to-green-600/10 rounded-full blur-3xl"></div>
      </div>
    </footer>
  );
};

export default Footer;
